import React, { useEffect } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import cartStatusAtom from '~/recoil/atom/cartStatus';
import memberInfoAtom from '~/recoil/atom/memberInfo';
import authStatusAtom from '~/recoil/atom/authStatus';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as fp from 'lodash/fp';
import { FirebaseDataStatus } from '~/types/utilities';
import useEnterpriseGroupBuyData from '~/hooks/useEnterpriseGroupBuyData';
import LoginModal from '~/components/EnterpriseGroupBuyComponents/LoginModal';
import UnableGroupNoticeModal from '~/components/EnterpriseGroupBuyComponents/UnableGroupNoticeModal';
import useEnterpriseGroupBuyCart from '~/hooks/useEnterpriseGroupBuyCart';
import { EnterpriseGroupBuyStatus } from '~/types/enterpriseGroupBuy';
import getPath from '~/utils/getNavigatePath';

export default function EnterpriseGroupBuyProvider({
  children,
}: {
  children?: React.ReactElement | null;
}) {
  const { enterpriseGroupBuyId = '' } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const globalEnterpriseGroupBuyData = useEnterpriseGroupBuyData(enterpriseGroupBuyId);
  const { handleInitEnterpriseGroupBuyCart } = useEnterpriseGroupBuyCart();

  const authStatus = useRecoilValue(authStatusAtom);
  const cartStatus = useRecoilValue(cartStatusAtom);
  const auth = useRecoilValue(authAtom);

  const setMemberInfo = useSetRecoilState(memberInfoAtom);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isGroupNoticeModalOpen,
    onClose: onGroupNoticeModalClose,
    onOpen: onGroupNoticeModalOpen,
  } = useDisclosure();

  useEffect(() => {
    if (
      fp.eq(authStatus, FirebaseDataStatus.INIT) ||
      fp.eq(globalEnterpriseGroupBuyData.cart, FirebaseDataStatus.INIT)
    ) {
      return;
    }

    // if (fp.eq(globalEnterpriseGroupBuyData.cart, FirebaseDataStatus.NOT_EXIST)) {
    //   onGroupNoticeModalOpen();
    // }

    // const isAfterOrderEndAt =
    //   globalEnterpriseGroupBuyData.data?.orderEndAt &&
    //   isBefore(globalEnterpriseGroupBuyData.data?.orderEndAt.toDate(), new Date());

    if (!auth) {
      setMemberInfo({
        name: '',
        id: '',
      });
      onOpen();
    } else {
      onClose();
      setMemberInfo({ id: auth.uid, name: auth?.displayName || '' });
    }

    if (globalEnterpriseGroupBuyData?.data) {
      if (auth) handleInitEnterpriseGroupBuyCart(globalEnterpriseGroupBuyData?.data, auth.uid);
      switch (globalEnterpriseGroupBuyData?.data?.status) {
        case EnterpriseGroupBuyStatus.ONGOING:
          return;
        case EnterpriseGroupBuyStatus.UNPAID:
        case EnterpriseGroupBuyStatus.WAITING:
        case EnterpriseGroupBuyStatus.RECEIVED:
          auth && navigate(getPath(`/enterpriseGroupBuy/${enterpriseGroupBuyId}/orderList`));
          return;
        case EnterpriseGroupBuyStatus.PICKED:
        case EnterpriseGroupBuyStatus.CANCELED:
          navigate(getPath(`/enterpriseGroupBuy/${enterpriseGroupBuyId}`));
          onGroupNoticeModalOpen();
          return;
        default:
          return;
      }
    }
  }, [
    pathname,
    authStatus,
    globalEnterpriseGroupBuyData?.cart,
    globalEnterpriseGroupBuyData?.data?.status,
    enterpriseGroupBuyId,
    auth,
    cartStatus,
  ]);

  if (!globalEnterpriseGroupBuyData?.data) return null;

  return (
    <>
      {children}
      <>
        <LoginModal
          enterpriseGroupBuyInfo={globalEnterpriseGroupBuyData.data}
          isOpen={isOpen}
          onClose={onClose}
        />
        <UnableGroupNoticeModal isOpen={isGroupNoticeModalOpen} />
      </>
    </>
  );
}
